/* eslint-disable */
/**
 * Converts arbitrary editor/pasted HTML into safe, email-canonical HTML.
 *
 * Isomorphic — runs identically in a browser (default) or Node (pass a
 * `parseHtml` adapter, see domAdapter.ts). Authors compose in a contentEditable
 * editor and may paste from Word, Excel, or Google Docs, so the output here is
 * built from a strict whitelist:
 *
 *  - Only known-safe tags survive; everything else is unwrapped to its children.
 *  - Scripts, styles, comments and event handlers never survive.
 *  - Each surviving tag gets a canonical base style, plus a filtered subset of
 *    author styling (font, size, color, weight, alignment, cell borders).
 *
 * The result renders consistently in email clients: inline styles only, no
 * classes, no stylesheets.
 */

import { browserParseHtml } from "./domAdapter";
import { ParseHtml } from "./domAdapter";
import { expandShorthandInHtml } from "./shorthandExpander";

/**
 * The exact shape this module reads off a parsed node — satisfied structurally
 * by both real DOM nodes and linkedom's nodes, without either needing to
 * nominally implement it. Using `any` here would also "work" but throws away
 * the typo-catching that matters most in code walking untrusted HTML.
 */
interface MinimalStyle {
  fontFamily?: string;
  fontSize?: string;
  color?: string;
  backgroundColor?: string;
  textAlign?: string;
  fontWeight?: string;
  fontStyle?: string;
  textDecorationLine?: string;
  textDecoration?: string;
  borderWidth?: string;
  borderTopWidth?: string;
  borderColor?: string;
  borderTopColor?: string;
  borderStyle?: string;
  borderTopStyle?: string;
}
interface MinimalNode {
  nodeType: number;
  nodeValue?: string | null;
  tagName: string;
  childNodes: ArrayLike<MinimalNode>;
  style: MinimalStyle;
  getAttribute(name: string): string | null;
}

const TAG_STYLES: Record<string, string> = {
  p: "margin:0 0 12px;font-size:14px;line-height:22px;color:#374151;",
  h2: "margin:18px 0 10px;font-size:18px;line-height:24px;font-weight:700;color:#111827;",
  h3: "margin:14px 0 8px;font-size:15px;line-height:21px;font-weight:600;color:#111827;",
  ul: "margin:0 0 12px;padding:0 0 0 22px;",
  ol: "margin:0 0 12px;padding:0 0 0 22px;",
  li: "margin:4px 0;font-size:14px;line-height:22px;color:#374151;",
  a: "color:#E85936;text-decoration:underline;",
  hr: "border:none;border-top:1px solid #E5E7EB;margin:16px 0;",
  blockquote:
    "margin:0 0 12px;padding:8px 14px;border-left:3px solid #E85936;background:#F9FAFB;font-size:14px;line-height:22px;color:#374151;",
  table: "width:100%;border-collapse:collapse;margin:0 0 14px;",
  th: "padding:8px 10px;border:1px solid #E5E7EB;background:#F9FAFB;font-size:12px;font-weight:600;color:#6B7280;text-align:left;",
  td: "padding:8px 10px;border:1px solid #E5E7EB;font-size:13px;line-height:20px;color:#374151;",
  strong: "font-weight:700;",
  em: "",
  u: "",
  s: "",
  span: "",
  br: "",
  thead: "",
  tbody: "",
  tr: "",
};

const TAG_MAP: Record<string, string> = {
  b: "strong",
  i: "em",
  strike: "s",
  del: "s",
  ins: "u",
  h1: "h2",
  h4: "h3",
  h5: "h3",
  h6: "h3",
  div: "p",
  font: "span",
};

const DROP_WITH_CONTENT = new Set([
  "script",
  "style",
  "head",
  "title",
  "meta",
  "link",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "textarea",
  "select",
]);
const LEGACY_FONT_SIZE: Record<string, number> = {
  "1": 10,
  "2": 12,
  "3": 14,
  "4": 16,
  "5": 18,
  "6": 22,
  "7": 26,
};

function esc(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Accepts hex or rgb()/rgba() and normalises to hex where possible, so output is
 * byte-identical regardless of which DOM engine (or browser) produced the color
 * string — real browsers serialise style-attribute colors back as rgb(), Node
 * DOM implementations pass hex through unchanged. rgba() with a fractional alpha
 * is left as-is (6-digit hex can't represent partial transparency, and #RRGGBBAA
 * isn't universally supported by email clients).
 */
function filterColor(value: string): string | null {
  const v = value.trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(v)) return v.toLowerCase();
  const rgbMatch = v.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/);
  if (rgbMatch) {
    const [r, g, b] = rgbMatch.slice(1).map((n) => parseInt(n, 10));
    const hex = (n: number) =>
      Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
    return `#${hex(r!)}${hex(g!)}${hex(b!)}`;
  }
  if (/^rgba\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*\)$/.test(v))
    return v;
  return null;
}

/**
 * Preserve the author's actual font choice (from paste or the font picker) rather
 * than collapsing to a handful of presets — enterprise authors expect "Calibri" to
 * stay "Calibri". Only the CSS-safe character set survives (no quotes/parens/
 * backslashes, so no declaration-breaking or url()/expression() injection), and
 * every family gets a sensible generic fallback appended so it still degrades
 * gracefully in clients that don't have the exact font installed.
 */
function filterFontFamily(value: string): string | null {
  const primaryRaw = value
    .split(",")[0]!
    .replace(/^['"]|['"]$/g, "")
    .trim();
  const primary = primaryRaw
    .replace(/[^a-zA-Z0-9 -]/g, "")
    .trim()
    .slice(0, 40);
  if (!primary) return null;
  const lower = primary.toLowerCase();
  if (/(georgia|times|cambria|garamond|palatino|book antiqua)/.test(lower)) {
    return `'${primary}',Georgia,'Times New Roman',serif`;
  }
  if (/(courier|consolas|mono|menlo|monaco)/.test(lower)) {
    return `'${primary}','Courier New',Courier,monospace`;
  }
  return `'${primary}',Arial,Helvetica,sans-serif`;
}

function parseFontSizeToPx(value: string): number | null {
  const m = value.match(/^(\d+(?:\.\d+)?)(px|pt)$/);
  if (!m) return null;
  const raw = parseFloat(m[1]!) * (m[2] === "pt" ? 1.3333 : 1);
  return Math.min(72, Math.max(8, Math.round(raw)));
}

function parseBorderWidthToPx(value: string): number | null {
  const m = value.match(/^(\d+(?:\.\d+)?)(px|pt)$/);
  if (!m) return null;
  const raw = parseFloat(m[1]!) * (m[2] === "pt" ? 1.3333 : 1);
  return Math.min(4, Math.max(1, Math.round(raw)));
}

function filterStyles(el: MinimalNode, tag: string): string {
  const out: string[] = [];
  const st = el.style;

  const family = st.fontFamily && filterFontFamily(st.fontFamily);
  if (family) out.push(`font-family:${family};`);

  const px = st.fontSize && parseFontSizeToPx(st.fontSize);
  if (px) out.push(`font-size:${px}px;line-height:${Math.round(px * 1.4)}px;`);

  const color = st.color && filterColor(st.color);
  if (color) out.push(`color:${color};`);
  const bg = st.backgroundColor && filterColor(st.backgroundColor);
  if (bg) out.push(`background-color:${bg};`);

  if (/^(left|center|right)$/.test(st.textAlign || ""))
    out.push(`text-align:${st.textAlign};`);

  const weightRaw = st.fontWeight;
  const weightNum =
    weightRaw === "bold"
      ? 700
      : weightRaw === "normal"
      ? 400
      : parseInt(weightRaw || "", 10);
  if (!Number.isNaN(weightNum) && weightNum >= 500) {
    const bucket = weightNum >= 750 ? 800 : weightNum >= 650 ? 700 : 600;
    out.push(`font-weight:${bucket};`);
  }

  if (st.fontStyle === "italic") out.push("font-style:italic;");
  const deco = st.textDecorationLine || st.textDecoration;
  if (deco?.includes("underline")) out.push("text-decoration:underline;");
  if (deco?.includes("line-through")) out.push("text-decoration:line-through;");

  if (tag === "td" || tag === "th") {
    const borderWidthRaw = st.borderWidth || st.borderTopWidth;
    const borderColorRaw = st.borderColor || st.borderTopColor;
    const borderStyleRaw = st.borderStyle || st.borderTopStyle || "";
    const widthPx = borderWidthRaw && parseBorderWidthToPx(borderWidthRaw);
    const borderColor = borderColorRaw && filterColor(borderColorRaw);
    const borderStyle = ["solid", "dashed", "dotted", "double"].includes(
      borderStyleRaw
    )
      ? borderStyleRaw
      : null;
    if (widthPx || borderColor || borderStyle) {
      out.push(
        `border:${widthPx || 1}px ${borderStyle || "solid"} ${
          borderColor || "#E5E7EB"
        };`
      );
    }
  }

  return out.join("");
}

function safeHref(href: string): string | null {
  const v = href.trim();
  return /^(https?:\/\/|mailto:)/i.test(v) ? v : null;
}

function serialize(node: MinimalNode): string {
  if (node.nodeType === 3 /* TEXT_NODE */) return esc(node.nodeValue || "");
  if (node.nodeType !== 1 /* ELEMENT_NODE */) return "";

  const el = node;
  let tag = el.tagName.toLowerCase();
  if (DROP_WITH_CONTENT.has(tag)) return "";
  tag = TAG_MAP[tag] || tag;

  const children = Array.from(el.childNodes).map(serialize).join("");
  if (!(tag in TAG_STYLES)) return children; // unknown tag → unwrap

  const extra: string[] = [];
  if (el.tagName.toLowerCase() === "font") {
    const face = el.getAttribute("face");
    const color = el.getAttribute("color");
    const size = el.getAttribute("size");
    const family = face && filterFontFamily(face);
    if (family) extra.push(`font-family:${family};`);
    if (color && filterColor(color)) extra.push(`color:${filterColor(color)};`);
    if (size && LEGACY_FONT_SIZE[size])
      extra.push(`font-size:${LEGACY_FONT_SIZE[size]}px;`);
  }

  const style = TAG_STYLES[tag] + filterStyles(el, tag) + extra.join("");
  const styleAttr = style ? ` style="${style}"` : "";

  if (tag === "br") return "<br>";
  if (tag === "hr") return `<hr style="${TAG_STYLES.hr}">`;

  if (!children.trim() && !["td", "th", "p"].includes(tag)) return "";
  if (tag === "p" && !children.trim()) return "";

  let attrs = "";
  if (tag === "a") {
    const href = safeHref(el.getAttribute("href") || "");
    if (!href) return children;
    attrs = ` href="${esc(href)}" target="_blank"`;
  }
  if (tag === "td" || tag === "th") {
    const colspan = parseInt(el.getAttribute("colspan") || "", 10);
    const rowspan = parseInt(el.getAttribute("rowspan") || "", 10);
    if (colspan > 1) attrs += ` colspan="${colspan}"`;
    if (rowspan > 1) attrs += ` rowspan="${rowspan}"`;
    return `<${tag}${attrs}${styleAttr}>${
      children.trim() ? children : "&nbsp;"
    }</${tag}>`;
  }

  return `<${tag}${attrs}${styleAttr}>${children}</${tag}>`;
}

/** Editor/pasted HTML → safe email HTML with canonical inline styles. Isomorphic — see domAdapter.ts. */
export function sanitizeEmailHtml(
  dirtyHtml: string,
  opts?: { parseHtml?: ParseHtml }
): string {
  const normalized = expandShorthandInHtml(dirtyHtml);
  const parseHtml = opts?.parseHtml ?? browserParseHtml;
  const doc = parseHtml(normalized);
  // ParsedDoc keeps childNodes as ArrayLike<unknown> since it must describe both
  // real DOM and linkedom nodes generically — MinimalNode is the shape this
  // module actually relies on, asserted once here at the adapter boundary.
  return Array.from(doc.body.childNodes as ArrayLike<MinimalNode>)
    .map(serialize)
    .join("");
}

/** True when the body has no visible content (used for validation). */
export function isEmailBodyEmpty(
  html: string,
  opts?: { parseHtml?: ParseHtml }
): boolean {
  const parseHtml = opts?.parseHtml ?? browserParseHtml;
  const doc = parseHtml(html);
  if (doc.body.querySelector("table, hr, img")) return false;
  return !(doc.body.textContent || "").trim();
}
